import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getAllInvitations,
  acceptInvitation,
  rejectInvitation,
  Invitation,
} from '../../services/api/team.service';
import { toast } from 'react-toastify';
import { Mail, CheckCircle, XCircle, Clock, Users, Shield, AlertCircle, X } from 'lucide-react';
import { useArtist } from '../../contexts/ArtistContext';
import { useUser } from '../../contexts/UserContext';
import { getFirstAccessibleRoute } from '../../utils/routePermissionUtils';

const UserInvitations: React.FC = () => {
  const navigate = useNavigate();
  const { refreshTeams, refreshInvitations, setCurrentArtistId, currentPermissions, pendingInvitations } = useArtist();
  const { userProfile } = useUser();
  const [invitations, setInvitations] = useState<Invitation[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [showSwitchPrompt, setShowSwitchPrompt] = useState(false);
  const [acceptedInvitation, setAcceptedInvitation] = useState<{ artistId: number; artistName: string; teamId: number } | null>(null);
  
  // Get system admin status
  const getSystemRole = (): string | null => {
    try {
      return sessionStorage.getItem('systemRole') || userProfile?.systemRole || null;
    } catch {
      return null;
    }
  };
  const isSystemAdmin = getSystemRole() === 'system_admin';

  // Helper function to check if there are remaining pending invitations and navigate if none
  const checkAndNavigateIfNoPending = async () => {
    // Wait a bit for invitations to refresh
    await new Promise(resolve => setTimeout(resolve, 800));
    
    // Refresh invitations to get latest count
    await refreshInvitations();
    
    // Refetch all invitations to get updated list
    await fetchAllInvitationData();
    
    // Wait a bit more for state to update
    await new Promise(resolve => setTimeout(resolve, 300));
    
    // Check if there are any pending invitations remaining (only active teams)
    // Use the updated invitations state
    const updatedPending = invitations.filter(
      (inv) => inv.invitationStatus === 'pending' && !isExpired(inv.expiresAt) && inv.teamIsActive === true
    );
    
    // If no pending invitations, navigate to first accessible category
    // Check both local state and context
    if (updatedPending.length === 0) {
      // Double check context as well (filter for active teams)
      await refreshInvitations();
      await new Promise(resolve => setTimeout(resolve, 200));
      
      const activePendingInContext = pendingInvitations?.filter(
        (inv) => inv.teamIsActive === true
      ) || [];
      
      if (activePendingInContext.length === 0) {
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
      }
    }
  };

  useEffect(() => {
    fetchAllInvitationData();
  }, []);

  // Monitor invitations and navigate if no pending invitations remain (only active teams)
  useEffect(() => {
    if (!loading && invitations.length > 0) {
      const hasPending = invitations.some(
        (inv) => inv.invitationStatus === 'pending' && !isExpired(inv.expiresAt) && inv.teamIsActive === true
      );
      
      // Filter context invitations for active teams
      const activePendingInContext = pendingInvitations?.filter(
        (inv) => inv.teamIsActive === true
      ) || [];
      
      // If no pending invitations in local state and context, navigate
      if (!hasPending && activePendingInContext.length === 0) {
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
      }
    }
  }, [invitations, pendingInvitations, loading, currentPermissions, isSystemAdmin, navigate]);

  const fetchAllInvitationData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Get all invitations from /api/invitations
      const allInvitations = await getAllInvitations();
      setInvitations(allInvitations);
      
      // Return the invitations for immediate use
      return allInvitations;
    } catch (err: any) {
      console.error('Error fetching invitations:', err);
      setError(err.message || 'Failed to fetch invitations');
      toast.error(err.message || 'Failed to fetch invitations');
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async (invitation: Invitation) => {
    if (!invitation.invitationToken) {
      toast.error('Invitation token not available. Please use the link from your email.');
      return;
    }

    if (isExpired(invitation.expiresAt)) {
      toast.error('This invitation has expired');
      return;
    }

    try {
      setProcessingId(invitation.invitationId);
      const result = await acceptInvitation(invitation.invitationToken, false);
      toast.success('Invitation accepted successfully!');
      
      // Refresh teams to update available artists in sidebar
      await refreshTeams();
      // Refresh invitations to update notification count immediately
      await refreshInvitations();
      
      // Fetch updated invitations list and get the result immediately
      const updatedInvitations = await fetchAllInvitationData();
      
      // Wait a bit for context to update
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshInvitations();
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Check if there are remaining pending invitations (excluding the one just accepted, only active teams)
      const remainingPending = updatedInvitations.filter(
        (inv) => inv.invitationStatus === 'pending' && 
                 !isExpired(inv.expiresAt) && 
                 inv.invitationId !== invitation.invitationId &&
                 inv.teamIsActive === true
      );
      
      // Also check context pendingInvitations (should be updated by now, filter for active teams)
      const activePendingInContext = pendingInvitations?.filter(
        (inv) => inv.teamIsActive === true
      ) || [];
      
      // If no pending invitations remain, navigate to first category (don't show switch prompt)
      if (remainingPending.length === 0 && activePendingInContext.length === 0) {
        // No pending invitations left, navigate to first accessible category
        // Don't switch artist context - user will do it manually
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
        return; // Exit early to prevent showing switch prompt
      }
      
      // Still have pending invitations, show switch prompt
      setAcceptedInvitation({
        artistId: invitation.artistId,
        artistName: invitation.artistName,
        teamId: result.teamId
      });
      setShowSwitchPrompt(true);
    } catch (err: any) {
      toast.error(err.message || 'Failed to accept invitation');
    } finally {
      setProcessingId(null);
    }
  };

  const handleSwitchToArtist = () => {
    if (acceptedInvitation) {
      // User manually chooses to switch - allow it
      setCurrentArtistId(acceptedInvitation.artistId);
      setShowSwitchPrompt(false);
      setAcceptedInvitation(null);
      // Wait for permissions to refresh, then check and navigate if no pending invitations
      setTimeout(async () => {
        await checkAndNavigateIfNoPending();
      }, 1000);
    }
  };

  const handleDismissSwitchPrompt = () => {
    setShowSwitchPrompt(false);
    // Don't switch artist - user dismissed the prompt
    // Check and navigate if no pending invitations remain
    if (acceptedInvitation) {
      setAcceptedInvitation(null);
      checkAndNavigateIfNoPending();
    }
  };

  const handleReject = async (invitation: Invitation) => {
    if (!invitation.invitationToken) {
      toast.error('Invitation token not available. Please use the link from your email.');
      return;
    }

    if (!window.confirm('Are you sure you want to reject this invitation?')) return;

    try {
      setProcessingId(invitation.invitationId);
      await rejectInvitation(invitation.invitationToken);
      toast.success('Invitation rejected');
      // Refresh invitations to update notification count immediately
      await refreshInvitations();
      
      // Fetch updated invitations list and get the result immediately
      const updatedInvitations = await fetchAllInvitationData();
      
      // Wait for context to update
      await new Promise(resolve => setTimeout(resolve, 500));
      await refreshInvitations();
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Check if there are remaining pending invitations (excluding the one just rejected, only active teams)
      const remainingPending = updatedInvitations.filter(
        (inv) => inv.invitationStatus === 'pending' && 
                 !isExpired(inv.expiresAt) && 
                 inv.invitationId !== invitation.invitationId &&
                 inv.teamIsActive === true
      );
      
      // Check context as well (filter for active teams)
      const activePendingInContext = pendingInvitations?.filter(
        (inv) => inv.teamIsActive === true
      ) || [];
      
      if (remainingPending.length === 0 && activePendingInContext.length === 0) {
        const firstRoute = getFirstAccessibleRoute(currentPermissions, isSystemAdmin);
        navigate(firstRoute);
      }
    } catch (err: any) {
      toast.error(err.message || 'Failed to reject invitation');
    } finally {
      setProcessingId(null);
    }
  };

  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const isExpired = (expiresAt: string): boolean => {
    return new Date(expiresAt) < new Date();
  };

  const getDaysUntilExpiry = (expiresAt: string): number => {
    const now = new Date();
    const expiry = new Date(expiresAt);
    const diffTime = expiry.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Helper function to get team status badge
  const getTeamStatusBadge = (teamIsActive: boolean | undefined) => {
    if (teamIsActive === undefined || teamIsActive === null) {
      return null; // Status unknown
    }
    if (teamIsActive === true) {
      return (
        <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-family-messina-sans flex items-center gap-1">
          <CheckCircle size={12} />
          Team Active
        </span>
      );
    } else {
      return (
        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-family-messina-sans flex items-center gap-1">
          <AlertCircle size={12} />
          Team Inactive
        </span>
      );
    }
  };

  // Helper function to render View Team button based on status
  const renderViewTeamButton = (invitation: Invitation) => {
    const isTeamActive = invitation.teamIsActive === true;
    
    if (isTeamActive) {
      return (
        <button
          onClick={() => navigate(`/teams/${invitation.teamId}`)}
          className="px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
        >
          View Team Details
        </button>
      );
    } else {
      // Team is inactive or deleted
      const statusText = invitation.teamIsActive === false ? 'Team is Inactive' : 'Team Deleted';
      return (
        <button
          disabled
          className="px-4 py-2 bg-gray-100 text-gray-500 rounded-lg cursor-not-allowed text-sm font-family-messina-sans opacity-60"
          title={statusText}
        >
          {statusText}
        </button>
      );
    }
  };

  if (loading) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Team Invitations
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-gray-600 font-family-messina-sans">Loading...</div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-nova-dashboard-900 min-h-screen">
        <div className="p-4 sm:p-6 md:p-8">
          <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem]">
            Team Invitations
          </span>
          <div className="flex items-center justify-center mt-[5rem]">
            <div className="text-red-600 font-family-messina-sans">{error}</div>
          </div>
        </div>
      </div>
    );
  }

  // Categorize invitations by invitationStatus
  // Show all pending invitations (including inactive teams) but count only active ones
  const allPendingInvitations = invitations.filter(
    (inv) => inv.invitationStatus === 'pending' && !isExpired(inv.expiresAt)
  );
  // Filter for count - only active teams
  const localPendingInvitations = allPendingInvitations.filter(
    (inv) => inv.teamIsActive === true
  );
  const expiredInvitations = invitations.filter(
    (inv) => inv.invitationStatus === 'pending' && isExpired(inv.expiresAt)
  );
  const acceptedInvitations = invitations.filter((inv) => inv.invitationStatus === 'accepted');
  const rejectedInvitations = invitations.filter((inv) => inv.invitationStatus === 'rejected');
  const revokedInvitations = invitations.filter((inv) => inv.invitationStatus === 'revoked');

  return (
    <div className="bg-nova-dashboard-900 min-h-screen">
      <div className="p-4 sm:p-6 md:p-8">
        <span className="text-[1.875rem] font-regular text-gray-900 block font-family-messina-modern mt-[3rem] mb-6">
          Team Invitations
        </span>

        {invitations.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
            <Mail className="mx-auto h-12 w-12 text-gray-400 mb-4" />
            <div className="text-gray-600 font-family-messina-sans mb-2">
              No invitations found
            </div>
            <p className="text-sm text-gray-500 font-family-messina-sans">
              You don't have any team invitations at the moment.
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {/* Pending Invitations */}
            {allPendingInvitations.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
                  Pending Invitations ({localPendingInvitations.length})
                </h2>
                <div className="space-y-4">
                  {allPendingInvitations.map((invitation) => {
                    const daysLeft = getDaysUntilExpiry(invitation.expiresAt);
                    return (
                      <div
                        key={invitation.invitationId}
                        className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 hover:shadow-md transition-shadow"
                      >
                        <div className="flex flex-col md:flex-row justify-between gap-4">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-3 flex-wrap">
                              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                                {invitation.teamName}
                              </h3>
                              <span className="px-2 py-1 bg-yellow-100 text-yellow-800 rounded text-xs font-family-messina-sans">
                                Pending
                              </span>
                              {getTeamStatusBadge(invitation.teamIsActive)}
                              {daysLeft <= 3 && daysLeft > 0 && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-family-messina-sans">
                                  Expires in {daysLeft} {daysLeft === 1 ? 'day' : 'days'}
                                </span>
                              )}
                            </div>

                            <div className="space-y-2 text-sm text-gray-600 font-family-messina-sans">
                              <div className="flex items-center gap-2">
                                <Users size={16} className="text-gray-400" />
                                <span>
                                  <span className="font-medium">Artist:</span> {invitation.artistName}
                                </span>
                              </div>
                              <div className="flex items-center gap-2">
                                <span className="font-medium">Invited by:</span> {invitation.inviterName}
                              </div>
                              {invitation.message && (
                                <div className="mt-3 p-3 bg-gray-50 rounded-lg italic">
                                  "{invitation.message}"
                                </div>
                              )}
                              <div className="flex flex-wrap gap-2 mt-3">
                                <Shield size={16} className="text-gray-400" />
                                <span className="font-medium">Roles:</span>
                                {invitation.roles.map((role) => (
                                  <span
                                    key={role}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-family-messina-sans"
                                  >
                                    {role}
                                  </span>
                                ))}
                              </div>
                              <div className="flex items-center gap-4 mt-4 text-xs text-gray-500">
                                <div className="flex items-center gap-1">
                                  <Clock size={14} />
                                  <span>Sent: {formatDate(invitation.sentAt)}</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <Clock size={14} />
                                  <span>Expires: {formatDate(invitation.expiresAt)}</span>
                                </div>
                              </div>
                            </div>
                          </div>

                          <div className="flex flex-col gap-2 md:min-w-[200px]">
                            {/* Only show Accept/Reject buttons if team is active */}
                            {invitation.teamIsActive === true && (
                              <>
                                <button
                                  onClick={() => handleAccept(invitation)}
                                  disabled={processingId === invitation.invitationId}
                                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-family-messina-sans flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <CheckCircle size={16} />
                                  <span>{processingId === invitation.invitationId ? 'Processing...' : 'Accept'}</span>
                                </button>
                                <button
                                  onClick={() => handleReject(invitation)}
                                  disabled={processingId === invitation.invitationId}
                                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-family-messina-sans flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                  <XCircle size={16} />
                                  <span>Reject</span>
                                </button>
                              </>
                            )}
                            {renderViewTeamButton(invitation)}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Expired Invitations */}
            {expiredInvitations.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
                  Expired Invitations ({expiredInvitations.length})
                </h2>
                <div className="space-y-4">
                  {expiredInvitations.map((invitation) => (
                    <div
                      key={invitation.invitationId}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 opacity-75"
                    >
                      <div className="flex items-center gap-3 mb-3 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                          {invitation.teamName}
                        </h3>
                        <span className="px-2 py-1 bg-red-100 text-red-800 rounded text-xs font-family-messina-sans flex items-center gap-1">
                          <AlertCircle size={12} />
                          Expired
                        </span>
                        {getTeamStatusBadge(invitation.teamIsActive)}
                      </div>
                      <div className="text-sm text-gray-600 font-family-messina-sans">
                        <p>
                          <span className="font-medium">Artist:</span> {invitation.artistName}
                        </p>
                        <p className="mt-1">
                          <span className="font-medium">Invited by:</span> {invitation.inviterName}
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          Expired on: {formatDate(invitation.expiresAt)}
                        </p>
                      </div>
                      <div className="mt-4">
                        {renderViewTeamButton(invitation)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Accepted Invitations */}
            {acceptedInvitations.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
                  Accepted Invitations ({acceptedInvitations.length})
                </h2>
                <div className="space-y-4">
                  {acceptedInvitations.map((invitation) => (
                    <div
                      key={invitation.invitationId}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6"
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                              {invitation.teamName}
                            </h3>
                            <span className="px-2 py-1 bg-green-100 text-green-800 rounded text-xs font-family-messina-sans">
                              Accepted
                            </span>
                            {getTeamStatusBadge(invitation.teamIsActive)}
                          </div>
                          <div className="space-y-1 text-sm text-gray-600 font-family-messina-sans">
                            <p>
                              <span className="font-medium">Artist:</span> {invitation.artistName}
                            </p>
                            <p>
                              <span className="font-medium">Invited by:</span> {invitation.inviterName}
                            </p>
                            {invitation.roles && invitation.roles.length > 0 && (
                              <div className="flex items-center gap-2 mt-2">
                                <Shield size={16} className="text-gray-400" />
                                <span className="font-medium">Roles:</span>
                                {invitation.roles.map((role) => (
                                  <span
                                    key={role}
                                    className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-family-messina-sans"
                                  >
                                    {role}
                                  </span>
                                ))}
                              </div>
                            )}
                            <p className="mt-2 text-xs text-gray-500">
                              Sent: {formatDate(invitation.sentAt)}
                            </p>
                            {invitation.respondedAt && (
                              <p className="mt-1 text-xs text-gray-500">
                                Accepted: {formatDate(invitation.respondedAt)}
                              </p>
                            )}
                          </div>
                        </div>
                        {renderViewTeamButton(invitation)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Rejected Invitations */}
            {rejectedInvitations.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
                  Rejected Invitations ({rejectedInvitations.length})
                </h2>
                <div className="space-y-4">
                  {rejectedInvitations.map((invitation) => (
                    <div
                      key={invitation.invitationId}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 opacity-75"
                    >
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                          {invitation.teamName}
                        </h3>
                        <span className="px-2 py-1 bg-gray-100 text-gray-800 rounded text-xs font-family-messina-sans">
                          Rejected
                        </span>
                        {getTeamStatusBadge(invitation.teamIsActive)}
                      </div>
                      <div className="text-sm text-gray-600 font-family-messina-sans">
                        <p>
                          <span className="font-medium">Artist:</span> {invitation.artistName}
                        </p>
                        <p className="mt-1">
                          <span className="font-medium">Invited by:</span> {invitation.inviterName}
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          Sent: {formatDate(invitation.sentAt)}
                        </p>
                        {invitation.respondedAt && (
                          <p className="mt-1 text-xs text-gray-500">
                            Rejected: {formatDate(invitation.respondedAt)}
                          </p>
                        )}
                      </div>
                      <div className="mt-4">
                        {renderViewTeamButton(invitation)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Revoked Invitations */}
            {revokedInvitations.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 mb-4 font-family-messina-modern">
                  Revoked Invitations ({revokedInvitations.length})
                </h2>
                <div className="space-y-4">
                  {revokedInvitations.map((invitation) => (
                    <div
                      key={invitation.invitationId}
                      className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 opacity-75"
                    >
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                          {invitation.teamName}
                        </h3>
                        <span className="px-2 py-1 bg-orange-100 text-orange-800 rounded text-xs font-family-messina-sans">
                          Revoked
                        </span>
                        {getTeamStatusBadge(invitation.teamIsActive)}
                      </div>
                      <div className="text-sm text-gray-600 font-family-messina-sans">
                        <p>
                          <span className="font-medium">Artist:</span> {invitation.artistName}
                        </p>
                        <p className="mt-1">
                          <span className="font-medium">Invited by:</span> {invitation.inviterName}
                        </p>
                        <p className="mt-2 text-xs text-gray-500">
                          Sent: {formatDate(invitation.sentAt)}
                        </p>
                        {invitation.respondedAt && (
                          <p className="mt-1 text-xs text-gray-500">
                            Revoked: {formatDate(invitation.respondedAt)}
                          </p>
                        )}
                      </div>
                      <div className="mt-4">
                        {renderViewTeamButton(invitation)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Prompt to switch to artist after accepting invitation */}
      {showSwitchPrompt && acceptedInvitation && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4">
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-gray-900 font-family-messina-modern">
                Switch to Artist?
              </h3>
              <button
                onClick={handleDismissSwitchPrompt}
                className="text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <X size={20} />
              </button>
            </div>
            
            <p className="text-gray-600 mb-6 font-family-messina-sans">
              You've accepted an invitation for <span className="font-medium">{acceptedInvitation.artistName}</span>. 
              Would you like to switch to this artist context now?
            </p>
            
            <div className="flex gap-3">
              <button
                onClick={handleSwitchToArtist}
                className="flex-1 px-4 py-2 bg-gray-800 text-white rounded-lg hover:bg-gray-700 transition-colors text-sm font-family-messina-sans"
              >
                Switch to Artist
              </button>
              <button
                onClick={handleDismissSwitchPrompt}
                className="flex-1 px-4 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors text-sm font-family-messina-sans"
              >
                Not Now
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserInvitations;

